import { HttpBackend, HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  catchError,
  defer,
  filter,
  finalize,
  map,
  mergeMap,
  of,
  shareReplay,
  take,
  tap,
  throwError,
} from 'rxjs';
import { environment } from '../../../../environments/environment';
import { RefreshTokenResponse } from '../models/auth.model';
import { TokenStorageService } from './token-storage.service';

type RefreshState =
  | { status: 'idle' }
  | { status: 'refreshing' }
  | { status: 'success'; token: string }
  | { status: 'error'; error: unknown };

/**
 * Coordonne le renouvellement du JWT pour toute l'application.
 *
 * - un seul appel HTTP de refresh peut être actif à la fois dans un onglet ;
 * - les appels concurrents attendent sur refreshState$ ;
 * - les onglets distincts sont absorbés côté API par la période de grâce
 *   idempotente du refresh token ;
 * - le nouveau token est stocké avant d'être diffusé aux requêtes en attente ;
 * - l'appel de refresh contourne les intercepteurs grâce à HttpBackend.
 */
@Injectable({ providedIn: 'root' })
export class TokenRefreshService {
  private readonly http = new HttpClient(inject(HttpBackend));
  private readonly tokenStorage = inject(TokenStorageService);

  private isRefreshing = false;
  private sessionGeneration = 0;
  private readonly refreshState$ = new BehaviorSubject<RefreshState>({ status: 'idle' });

  /**
   * Installe immédiatement un JWT issu d'une authentification réussie.
   * Toute ancienne rotation encore en vol devient alors obsolète et ne peut
   * plus remplacer ni invalider la nouvelle session.
   */
  adoptAccessToken(token: string): void {
    const normalized = token.trim();
    if (!normalized) return;

    this.sessionGeneration += 1;
    this.isRefreshing = false;
    this.tokenStorage.setToken(normalized);
    this.refreshState$.next({ status: 'success', token: normalized });
  }

  refreshAccessToken(): Observable<string> {
    return defer(() => {
      if (this.isRefreshing) {
        return this.waitForRefreshResult();
      }

      this.isRefreshing = true;
      const refreshGeneration = this.sessionGeneration;
      this.refreshState$.next({ status: 'refreshing' });

      return this.http
        .post<RefreshTokenResponse>(
          `${environment.apiUrl}/token/refresh`,
          {},
          { withCredentials: true },
        )
        .pipe(
          map((response) => {
            const token = response?.token?.trim();
            if (!token) {
              throw new Error('La réponse de renouvellement ne contient aucun token.');
            }

            // Un login peut avoir installé un JWT pendant cette requête.
            // Dans ce cas, la réponse ancienne ne doit jamais l'écraser.
            if (refreshGeneration !== this.sessionGeneration) {
              const latestToken = this.tokenStorage.getToken();
              if (latestToken && !this.tokenStorage.isExpired(latestToken)) {
                return latestToken;
              }
            }
            return token;
          }),
          tap((token) => {
            if (refreshGeneration === this.sessionGeneration) {
              // Ordre impératif : persister d'abord, réveiller la file ensuite.
              this.tokenStorage.setToken(token);
            }
            this.refreshState$.next({ status: 'success', token });
          }),
          catchError((error: unknown) => {
            if (refreshGeneration !== this.sessionGeneration) {
              const latestToken = this.tokenStorage.getToken();
              if (latestToken && !this.tokenStorage.isExpired(latestToken)) {
                this.refreshState$.next({ status: 'success', token: latestToken });
                return of(latestToken);
              }
            }

            // Toutes les requêtes bloquées reçoivent le même échec et peuvent
            // terminer proprement au lieu de rester suspendues indéfiniment.
            this.refreshState$.next({ status: 'error', error });
            return throwError(() => error);
          }),
          finalize(() => {
            if (refreshGeneration === this.sessionGeneration) {
              this.isRefreshing = false;
            }
          }),
          // Le refresh continue même si la requête ayant déclenché le premier
          // 401 est désabonnée ; les autres requêtes de la file restent servies.
          shareReplay({ bufferSize: 1, refCount: false }),
        );
    });
  }

  revokeRefreshToken(): Observable<void> {
    return this.http
      .post<void>(`${environment.apiUrl}/token/logout`, {}, { withCredentials: true })
      .pipe(catchError(() => of(void 0)));
  }

  private waitForRefreshResult(): Observable<string> {
    return this.refreshState$.pipe(
      filter(
        (state): state is Extract<RefreshState, { status: 'success' | 'error' }> =>
          state.status === 'success' || state.status === 'error',
      ),
      take(1),
      mergeMap((state) =>
        state.status === 'success' ? of(state.token) : throwError(() => state.error),
      ),
    );
  }
}
